import type { MaillonDeBoucle } from '../../partage/api/types';

/**
 * Le dessin du refus d'une boucle, sans rendu (chantier nettoyage-regles, ticket 01) : où se
 * posent les cartes des maillons, les arcs ambrés qui les joignent, l'annotation du lien tenté ;
 * et le fil qui dit où vit chaque maillon. `RefusDeCycle` ne fait que le rendu ; l'ordre des
 * maillons vient de `tourDeBoucle`. Testé dans `tests/client/moteur/conditions/dessinDeLaBoucle.test.ts`.
 */

// ── Le fil d’un maillon ─────────────────────────────────────────────────────

/**
 * « Bali › Logement » : le dossier puis les bulles qui mènent au maillon. Un dossier se dit tel
 * (« Un projet » au téléphone, `motsDuDossier`).
 */
export function filDe(maillon: MaillonDeBoucle, unDossier: string): string {
  return maillon.chemin.length > 0 ? maillon.chemin.map((c) => c.titre).join(' › ') : unDossier;
}

// ── La géométrie du dessin ───────────────────────────────────────────────────

export type Place = { x: number; y: number };
export type Arc = { trace: string; pointe: string };
export type Plan = { largeur: number; hauteur: number; places: Place[]; arcs: Arc[]; annotation: Place };

export const LARGEUR_CARTE = 218;
export const HAUTEUR_CARTE = 86;

/**
 * Trois maillons : le dessin de la planche, aux pixels près. C'est le cas qu'elle montre, et
 * ses courbes sont mieux posées que ce qu'un calcul produit.
 */
const PLANCHE: Plan = {
  largeur: 828,
  hauteur: 322,
  places: [
    { x: 96, y: 72 },
    { x: 528, y: 72 },
    { x: 312, y: 220 },
  ],
  arcs: [
    { trace: 'M312 92 C 400 58, 466 62, 528 100', pointe: 'M522 92 l 14 10 -16 8' },
    { trace: 'M596 164 C 596 210, 542 244, 476 248', pointe: 'M488 240 l -14 9 14 9' },
    { trace: 'M306 240 C 240 234, 208 204, 218 162', pointe: 'M210 174 l 8 -14 10 12' },
  ],
  annotation: { x: 105, y: 213 },
};

/**
 * Au-delà de trois maillons — et une vraie boucle administrative en compte volontiers cinq —
 * on dispose les cartes sur une ellipse et on garde la même grammaire : arcs ambrés bombés
 * vers l'extérieur, pointes dessinées, dernier arc en pointillé.
 */
export function disposer(combien: number): Plan {
  if (combien === 3) return PLANCHE;

  /*
   * On écarte l'ellipse tant que deux maillons voisins se touchent. Le nombre de maillons
   * n'est pas connu d'avance — une boucle administrative en compte volontiers cinq — et
   * deux cartes qui se chevauchent rendraient illisible la seule chose que l'écran a à
   * apprendre.
   */
  let rx = 260;
  for (let essai = 0; essai < 30 && combien >= 2 && !assezEcarte(combien, rx); essai += 1) {
    rx *= 1.1;
  }
  const ry = rx * 0.55;

  const cx = rx + LARGEUR_CARTE / 2 + 26;
  const cy = ry + HAUTEUR_CARTE / 2 + 26;
  const centre = { x: cx, y: cy };
  const centres = surLEllipse(combien, cx, cy, rx, ry);

  const arcs: Arc[] = [];
  let annotation = centre;

  for (let i = 0; i < combien && combien >= 2; i += 1) {
    const depuis = centres[i];
    const vers = centres[(i + 1) % combien];
    if (!depuis || !vers) continue;
    const { arc, milieu } = arcEntre(depuis, vers, centre);
    arcs.push(arc);
    if (i === combien - 1) annotation = milieu;
  }

  return {
    largeur: cx * 2,
    hauteur: cy * 2,
    places: centres.map((c) => ({ x: c.x - LARGEUR_CARTE / 2, y: c.y - HAUTEUR_CARTE / 2 })),
    arcs,
    annotation,
  };
}

function surLEllipse(combien: number, cx: number, cy: number, rx: number, ry: number): Place[] {
  return Array.from({ length: combien }, (_, i) => {
    const angle = (i / combien) * 2 * Math.PI - Math.PI / 2;
    return { x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) };
  });
}

/** Deux maillons voisins laissent-ils la place d'un arc entre eux&nbsp;? */
function assezEcarte(combien: number, rx: number): boolean {
  const centres = surLEllipse(combien, 0, 0, rx, rx * 0.55);
  return centres.every((a, i) => {
    const b = centres[(i + 1) % combien];
    if (!b) return true;
    const n = Math.hypot(b.x - a.x, b.y - a.y);
    if (n === 0) return false;
    return n >= portee((b.x - a.x) / n, (b.y - a.y) / n) * 2 + 70;
  });
}

/** La demi-largeur d'une carte dans une direction donnée. */
export function portee(ux: number, uy: number): number {
  const tx = ux === 0 ? Number.POSITIVE_INFINITY : LARGEUR_CARTE / 2 / Math.abs(ux);
  const ty = uy === 0 ? Number.POSITIVE_INFINITY : HAUTEUR_CARTE / 2 / Math.abs(uy);
  return Math.min(tx, ty);
}

/** Le point où la droite qui joint deux cartes sort du rectangle de la première. */
function bord(centre: Place, ux: number, uy: number, marge: number): Place {
  const t = portee(ux, uy) + marge;
  return { x: centre.x + ux * t, y: centre.y + uy * t };
}

function arcEntre(a: Place, b: Place, centre: Place): { arc: Arc; milieu: Place } {
  const n = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const ux = (b.x - a.x) / n;
  const uy = (b.y - a.y) / n;

  const depart = bord(a, ux, uy, 6);
  const arrivee = bord(b, -ux, -uy, 10);

  const mx = (depart.x + arrivee.x) / 2;
  const my = (depart.y + arrivee.y) / 2;

  // L'arc se bombe VERS L'EXTÉRIEUR du cercle, sinon il passe sous les cartes. À deux
  // maillons, le milieu tombe sur le centre : les deux arcs se bombent alors chacun de
  // son côté, sans quoi l'aller et le retour se superposeraient.
  let vx = mx - centre.x;
  let vy = my - centre.y;
  let vn = Math.hypot(vx, vy) || 1;
  if (Math.abs((vx * uy - vy * ux) / vn) < 0.25) {
    vx = -uy;
    vy = ux;
    vn = 1;
  }

  const pilote = { x: mx + (vx / vn) * 34, y: my + (vy / vn) * 34 };
  const angle = Math.atan2(arrivee.y - pilote.y, arrivee.x - pilote.x);

  return {
    arc: {
      trace: `M${r(depart.x)} ${r(depart.y)} Q${r(pilote.x)} ${r(pilote.y)} ${r(arrivee.x)} ${r(arrivee.y)}`,
      pointe: pointeDe(arrivee, angle),
    },
    milieu: { x: mx + (vx / vn) * 62, y: my + (vy / vn) * 62 },
  };
}

/** Une pointe ouverte, jamais un triangle plein : la même que celle de la planche. */
function pointeDe(p: Place, angle: number): string {
  const a = angle + Math.PI - 0.42;
  const b = angle + Math.PI + 0.42;
  return `M${r(p.x + 13 * Math.cos(a))} ${r(p.y + 13 * Math.sin(a))} L${r(p.x)} ${r(p.y)} L${r(p.x + 13 * Math.cos(b))} ${r(p.y + 13 * Math.sin(b))}`;
}

function r(valeur: number): number {
  return Math.round(valeur * 10) / 10;
}
